import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core'
import { NgbDate, NgbDateStruct, Placement } from '@ng-bootstrap/ng-bootstrap'
import { formatNgbDateStruct } from './date-picker.functions'
import { SegmentService } from '../../../shared/analytics/services/segment.service'
import { SegmentCalendarDateClicked, SegmentEvents } from '../../../shared/analytics/models/segment-events.model'
import { UntypedFormControl } from '@angular/forms'
import { takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { getYear } from 'date-fns'
import { AnalyticsModel } from '@flocasts/experience-service-types'
import { buildTrackCallProperties } from '../../../shared/analytics/directives/analytics.directive.utility'

/**
 * @see {https://ng-bootstrap.github.io/#/components/datepicker/overview}
 */
@Component({
  selector: 'flo-date-picker',
  templateUrl: './date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatePickerComponent implements OnInit, OnDestroy {
  private readonly ngOnDestroy$ = new Subject<void>()
  private _selectedDate: NgbDateStruct | null
  public datePickerFormControl = new UntypedFormControl()

  @Input() analyticsProperties: AnalyticsModel | null

  // The Event List Controls adds 'bottom-right' to fix CXP-910.
  // There was a bug in Safari and mobile viewports that caused the datepicker to render off-screen
  @Input() placement: Placement = 'bottom-left'

  @Input()
  set selectedDate(selectedDate: NgbDateStruct | null) {
    if (selectedDate) {
      this._selectedDate = selectedDate
      this.startDate = this._selectedDate
      this.datePickerFormControl.setValue(this.selectedDate)
    }
  }

  get selectedDate(): NgbDateStruct | null {
    return this._selectedDate
  }

  /** The earliest date that can be displayed or selected. */
  @Input() minDate: NgbDateStruct = new NgbDate(1901, 1, 1)

  /**
   * The last date that can be displayed or selected.
   * Defaulted to three years out.
   */
  @Input() maxDate: NgbDateStruct = new NgbDate(getYear(new Date()) + 3, 12, 30)

  /** The date to open calendar with,
   * not to be confused with the selectedDate.
   * Defaults to the current month if not specified,
   * or the month of the selectedDate.
   */
  @Input() startDate: NgbDateStruct

  /**
   * An event emitted when user selects a date using keyboard or mouse.
   */
  @Output()
  dateSelected = new EventEmitter<{ formattedDate: string; day: number; month: number; year: number }>()

  constructor(private readonly segmentService: SegmentService) {}

  ngOnInit(): void {
    this.datePickerFormControl.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe((date: NgbDateStruct) => {
      if (
        !!this.selectedDate &&
        this.selectedDate.year === date.year &&
        this.selectedDate.month === date.month &&
        this.selectedDate.day === date.day
      ) {
        return
      }
      this.selectedDate = date
      this.startDate = date
      const formattedDate = formatNgbDateStruct(date)
      this.selectTrackCall(formattedDate)
      this.dateSelected.emit({ formattedDate, day: date.day, month: date.month, year: date.year })
    })
  }

  private selectTrackCall(formattedDate: string) {
    const SEGMENT_CALENDAR: SegmentCalendarDateClicked = {
      name: SegmentEvents.CALENDAR_DATE_CLICKED,
      properties: this.analyticsProperties
        ? {
            ...buildTrackCallProperties(this.analyticsProperties),
            name: formattedDate
          }
        : {
            name: formattedDate
          }
    }
    this.segmentService.trackEvent(SEGMENT_CALENDAR)
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
